import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useContact } from '../../contexts/ContactContext';
import {
  LayoutDashboard,
  Sprout,
  Users,
  ShoppingCart,
  CreditCard,
  BarChart3,
  Menu,
  X,
  LogOut,
  User,
  Phone,
  UserCheck,
  Truck,
  Calendar as CalendarIcon,
  Plus,
  Sun,
  Moon,
  MapPin,
  ExternalLink,
  Settings,
  Mail,
  AlertTriangle,
  Warehouse
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { contactInfo } = useContact();
  const location = useLocation();

  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Orders', href: '/orders', icon: ShoppingCart },
    { name: 'Seeding Planning', href: '/seeding', icon: CalendarIcon },
    { name: 'Plants', href: '/plants', icon: Sprout },
    { name: 'Inventory', href: '/inventory', icon: Warehouse },
    { name: 'Farmers', href: '/customers', icon: Users },
    { name: 'Deliveries', href: '/deliveries', icon: Truck },
    { name: 'Payments', href: '/payments', icon: CreditCard },
    { name: 'Reports', href: '/reports', icon: BarChart3 },
  ];

  const bottomNavigation = [
    { name: 'Workers', href: '/workers', icon: UserCheck },
    { name: 'Admin', href: '/admin', icon: Settings },
  ];

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    try {
      await logout();
      setShowLogoutConfirm(false);
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-800">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-2 overflow-hidden border border-gray-200 dark:border-gray-600">
                <img src={`${process.env.PUBLIC_URL}/nursery_logo.png`} alt="Kariyas Nursery" className="w-8 h-8 object-contain" />
              </div>
              <h1 className="text-lg font-bold text-green-600 dark:text-green-400">Kariyas Nursery</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {mainNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  } transition-colors duration-200`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 dark:border-gray-700 px-2 py-4">
            {bottomNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  } transition-colors duration-200`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <div className="flex h-16 items-center px-4">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-2 overflow-hidden border border-gray-200 dark:border-gray-600">
              <img src={`${process.env.PUBLIC_URL}/nursery_logo.png`} alt="Kariyas Nursery" className="w-8 h-8 object-contain" />
            </div>
            <h1 className="text-lg font-bold text-green-600 dark:text-green-400">Kariyas Nursery</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {mainNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  } transition-colors duration-200`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 dark:border-gray-700 px-2 py-4">
            {bottomNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  } transition-colors duration-200`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-2 sm:gap-x-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 sm:px-6 lg:px-8 shadow-sm transition-colors duration-200">
          <button
            type="button"
            className="-m-2 p-2 text-gray-700 dark:text-gray-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-2 sm:gap-x-4">
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center w-8 h-8 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5" />
                ) : (
                  <Sun className="h-5 w-5" />
                )}
              </button>
              <div className="hidden sm:flex items-center gap-x-2">
                <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{currentUser?.email}</span>
              </div>
              <button
                onClick={handleLogoutClick}
                className="flex items-center gap-x-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <LogOut className="h-5 w-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6 bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>

        {/* Modern Footer */}
        <footer className="bg-gradient-to-r from-gray-800 to-gray-900 dark:from-gray-900 dark:to-black text-white mt-16 transition-all duration-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="md:col-span-2 text-left">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-3 overflow-hidden">
                    <img src={`${process.env.PUBLIC_URL}/nursery_logo.png`} alt="Kariyas Nursery" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="text-2xl font-bold text-white dark:text-gray-100">Kariyas Nursery</h3>
                </div>
                <p className="text-gray-300 dark:text-gray-300 mb-4 max-w-md text-left">
                  Premium & High Quality Plants.
                  Empowering farmers with technology-driven solutions for sustainable agriculture.
                </p>
                <div className="flex flex-col space-y-2">
                  <a
                    href={`tel:${contactInfo.primaryPhone.replace(/\s/g, '')}`}
                    className="flex items-center text-green-400 dark:text-green-300 hover:text-green-300 dark:hover:text-green-200 transition-colors"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {contactInfo.primaryPhone}
                  </a>
                  <a
                    href={`tel:${contactInfo.secondaryPhone.replace(/\s/g, '')}`}
                    className="flex items-center text-green-400 dark:text-green-300 hover:text-green-300 dark:hover:text-green-200 transition-colors"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {contactInfo.secondaryPhone}
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-semibold text-white dark:text-gray-200 mb-4">Quick Links</h4>
                <div className="space-y-2">
                  <Link to="/dashboard" className="text-gray-300 dark:text-gray-200 hover:text-green-400 dark:hover:text-green-300 transition-colors block">
                    Dashboard
                  </Link>
                  <Link to="/orders" className="text-gray-300 dark:text-gray-200 hover:text-green-400 dark:hover:text-green-300 transition-colors block">
                    Orders
                  </Link>
                  <Link to="/customers" className="text-gray-300 dark:text-gray-200 hover:text-green-400 dark:hover:text-green-300 transition-colors block">
                    Farmers
                  </Link>
                  <Link to="/plants" className="text-gray-300 dark:text-gray-200 hover:text-green-400 dark:hover:text-green-300 transition-colors block">
                    Plants
                  </Link>
                </div>
              </div>

              {/* Contact Info */}
              <div className="text-left">
                <h4 className="text-lg font-semibold text-white dark:text-gray-200 mb-4">Contact</h4>
                <div className="space-y-3 text-gray-300 dark:text-gray-200">
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-left">{contactInfo.address}</span>
                  </div>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-start text-green-400 dark:text-green-300 hover:text-green-300 dark:hover:text-green-200 transition-colors text-sm"
                  >
                    <Mail className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{contactInfo.email}</span>
                  </a>
                  <a
                    href={contactInfo.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start text-green-400 dark:text-green-300 hover:text-green-300 dark:hover:text-green-200 transition-colors text-sm"
                  >
                    <ExternalLink className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>View on Google Maps</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-8 pt-8 border-t border-gray-700 dark:border-gray-600">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <p className="text-sm text-gray-400 dark:text-gray-400">
                  © 2026 Kariyas Nursery. All rights reserved.
                </p>
                <div className="flex items-center space-x-4 mt-4 md:mt-0">
                  <a 
                    href="https://twinstech.in/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-gray-400 dark:text-gray-300 hover:text-green-400 dark:hover:text-green-300 transition-colors"
                  >
                    Powered by Twinstech.in
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Floating Action Button for Quick Actions */}
        {(location.pathname === '/dashboard') && (
          <div className="fixed bottom-6 right-6 z-50">
            <div className="flex flex-col space-y-2">
              <Link
                to="/orders"
                className="flex items-center justify-center w-12 h-12 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all hover:scale-110"
                title="New Order"
              >
                <ShoppingCart className="h-5 w-5" />
              </Link>
              <Link
                to="/customers"
                className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all hover:scale-110"
                title="New Farmer"
              >
                <Users className="h-5 w-5" />
              </Link>
            </div>
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Confirm Logout</h3>
                    <p className="text-amber-100 text-sm">You are about to sign out</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to logout? You will need to sign in again to access the application.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={cancelLogout}
                    className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmLogout}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all flex items-center justify-center gap-2"
                  >
                    <LogOut className="h-5 w-5" />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout; 